import React, { useRef, useState } from 'react'
import { CheckoutAccordion, CheckoutAccordionRefType, BaseTextArea, Icon, getTextColorByBrightness } from '@shopflo/ui'
import { Gift, Trash, WarningCircle, Note, Plus, IconWeight, Paperclip } from '@phosphor-icons/react'
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import { customizeCartUIType } from 'src/lib/types/cartUI'

// Hardcoded data
const CART_NOTE_CONFIG: {
  title: string
  icon: CartNoteIcon
  suggestionText: string
  type: 'FREE' | 'PAID'
  price: string
} = {
  title: 'Add a note',
  icon: 'EDIT',
  suggestionText: 'Add gift message or delivery instructions...',
  type: 'FREE', // Change to "PAID" to show price
  price: '10'
}

type CartNoteIcon = 'ATTACH' | 'EDIT' | 'GIFT'

const truncateString = (str: string, maxLength: number) => {
  if (str.length <= maxLength) return str
  return str.substring(0, maxLength) + '...'
}

interface CartNotesProps {
  notesConfig: customizeCartUIType['notesConfig'] & { buttonColor: string; buttonTextColor: string }
}

const CartNotes: React.FC<CartNotesProps> = ({ notesConfig }) => {
  const cartNoteRef = useRef<CheckoutAccordionRefType>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [showError, setShowError] = useState(false)
  const [hasAppliedNote, setHasAppliedNote] = useState(false)
  const [appliedNoteText, setAppliedNoteText] = useState('')

  const getCartNoteIcon = (
    icon: CartNoteIcon,
    {
      active,
      mode = 'regular'
    }: {
      active: boolean
      mode?: IconWeight
    }
  ) => {
    let IconComponent = null
    switch (icon) {
      case 'ATTACH':
        IconComponent = Paperclip
        break
      case 'EDIT':
        IconComponent = Note
        break
      case 'GIFT':
        IconComponent = Gift
        break
      default:
        return null
    }
    return (
      <Icon
        IconComponent={IconComponent}
        iconProps={{
          weight: mode
        }}
        size='sm'
        style={{
          color: active ? hexToRGBA(notesConfig.textColor, 0.6) : notesConfig.textColor
        }}
      />
    )
  }

  const handleCancel = () => {
    if (isEditMode) {
      setIsEditMode(false)
      setInputValue(appliedNoteText)
    } else {
      setInputValue('')
    }
    setShowError(false)
    cartNoteRef?.current?.closeAccordion()
  }

  const handleSave = () => {
    if (!inputValue.trim()) {
      setShowError(true)
      return
    }
    setAppliedNoteText(inputValue)
    setHasAppliedNote(true)
    setIsEditMode(false)
    cartNoteRef?.current?.closeAccordion()
  }

  const handleEdit = () => {
    setIsEditMode(true)
    cartNoteRef?.current?.openAccordion()
  }

  const handleDelete = () => {
    setInputValue('')
    setAppliedNoteText('')
    setHasAppliedNote(false)
    setIsEditMode(false)
  }

  const renderAppliedCartNote = () => {
    return (
      <div
        className='flex flex-col gap-2 p-3 border border-gray-300 rounded-xl'
        onClick={handleEdit}
        style={{
          backgroundColor: notesConfig.backgroundColor,
          borderRadius: notesConfig.cardRadius,
          borderColor: notesConfig.borderColor,
          ...(notesConfig.cardBorderSides === 'bottom'
            ? {
                borderBottomWidth: notesConfig.cardBorderWidth,
                borderBottomColor: notesConfig.borderColor,
                borderBottomStyle: 'solid' as const
              }
            : {
                borderWidth: notesConfig.cardBorderWidth,
                borderColor: notesConfig.borderColor,
                borderStyle: 'solid' as const
              })
        }}
      >
        <div className='flex justify-between items-center'>
          <div className='flex gap-2 items-center'>
            {getCartNoteIcon(CART_NOTE_CONFIG.icon, { active: true, mode: 'fill' })}
            <p
              className='text-sm text-gray-600 text-ellipsis'
              style={{
                color: notesConfig.textColor
              }}
            >
              {truncateString(appliedNoteText, 50)}
            </p>
          </div>
          <div className='flex'>
            <button onClick={handleDelete} className='text-sm flex items-center gap-1'>
              <Icon IconComponent={Trash} size='sm' iconProps={{ color: notesConfig.textColor }} />
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderCartNoteTile = () => {
    return (
      <CheckoutAccordion
        id={'cart_notes'}
        ref={cartNoteRef}
        defaultOpen={false}
        renderTitleIcon={(active: boolean) => getCartNoteIcon(CART_NOTE_CONFIG.icon, { active }) ?? <div></div>}
        renderTitleText={(active: boolean) => (
          <div
            className='text-base flex gap-2 items-center'
            style={{
              color: notesConfig.backgroundColor
            }}
          >
            <h3
              className={`${active ? 'text-text-xs-medium opacity-60' : 'text-text-sm-medium'}`}
              style={{
                color: active ? hexToRGBA(notesConfig.textColor, 0.6) : notesConfig.textColor
              }}
            >
              {CART_NOTE_CONFIG.title}
            </h3>
            {CART_NOTE_CONFIG.type === 'PAID' && (
              <p
                className='text-text-sm-medium text-zinc-500'
                style={{
                  color: notesConfig.textColor
                }}
              >
                (₹{CART_NOTE_CONFIG.price})
              </p>
            )}
          </div>
        )}
        callbackOnToggle={(isOpen: boolean) => {
          if (!isOpen) {
            setIsEditMode(false)
          }
        }}
        buttonClass='!h-auto !py-3 !rounded-lg !bg-transparent'
        customClass='!mx-0 !mt-0 !bg-transparent !rounded-lg'
        renderAccordionIcon={(active: boolean) =>
          active ? null : (
            <Icon IconComponent={Plus} size='sm' iconProps={{ weight: 'bold', color: notesConfig.textColor }} />
          )
        }
        content={
          <div className='flex flex-col px-3 pb-3 gap-3'>
            <div
              className='flex w-full flex-col gap-2'
              style={
                {
                  ['--placeholder-color' as string]: hexToRGBA(notesConfig.textColor, 0.8),
                  ['--text-color' as string]: notesConfig.textColor
                } as React.CSSProperties
              }
            >
              <BaseTextArea
                variant='ghost'
                dimension='xs'
                value={inputValue}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  setShowError(false)
                  setInputValue(e.target.value)
                }}
                error={showError}
                placeholder={CART_NOTE_CONFIG.suggestionText}
                textAreaClassName='!bg-transparent [&::placeholder]:![color:var(--placeholder-color)] ![color:var(--text-color)]'
              />
              {showError && (
                <p className='text-xs font-medium text-red-500 flex flex-row items-center gap-1'>
                  <WarningCircle size={14} />
                  Please enter a note
                </p>
              )}
            </div>
            <div className='flex gap-2 justify-end'>
              <button
                style={{
                  borderColor: getTextColorByBrightness({ bgHexColor: notesConfig.backgroundColor, lightOpacity: 24 }),
                  color: notesConfig.textColor
                }}
                className='px-4 py-1 rounded-lg text-text-sm-semibold border text-zinc-600'
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                style={{
                  backgroundColor: notesConfig.buttonColor,
                  color: notesConfig.buttonTextColor
                }}
                className='px-4 py-1  text-white rounded-lg text-text-sm-semibold'
                onClick={handleSave}
              >
                Save
              </button>
            </div>
          </div>
        }
      />
    )
  }

  return (
    <div>
      {hasAppliedNote && !isEditMode && renderAppliedCartNote()}
      {(!hasAppliedNote || isEditMode) && (
        <div
          className='overflow-hidden'
          style={{
            backgroundColor: notesConfig.backgroundColor,
            borderRadius: notesConfig.cardRadius,
            borderColor: notesConfig.borderColor,
            ...(notesConfig.cardBorderSides === 'bottom'
              ? {
                  borderBottomWidth: notesConfig.cardBorderWidth,
                  borderBottomColor: notesConfig.borderColor,
                  borderBottomStyle: 'solid' as const
                }
              : {
                  borderWidth: notesConfig.cardBorderWidth,
                  borderColor: notesConfig.borderColor,
                  borderStyle: 'solid' as const
                })
          }}
        >
          {renderCartNoteTile()}
        </div>
      )}
    </div>
  )
}

export default CartNotes
